import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import ThresholdNote from './ThresholdNote';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import FlagSummary from './FlagSummary';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

// SFR results for the selected month - a single global threshold, so every
// row is compared against the same number (unlike LCR/ACR/LTA's
// per-department/category thresholds). Only people with an Assessment
// Target set for the period appear at all.
const SfrDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [thresholdNote, setThresholdNote] = useState(null); // why there is no threshold for this month, when there isn't
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/sfr-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load SFR results');
          return;
        }
        setRows(res?.data?.rows || []);
        setThresholdNote(res?.data?.thresholdNote || null);
        setSummary({
          totalEvaluated: res?.data?.totalEvaluated || 0,
          warningCount: res?.data?.warningCount || 0,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load SFR results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.threshold === null ? -Infinity : row.threshold - row.value);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)),
    [rows]
  );

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography
          variant="body2"
          fontStyle={row.isActive === false ? 'italic' : 'normal'}
          color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}
        >
          {row.entityName}
          {row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'targetAssessment', headerName: 'Assessment Target', align: 'right' },
    { field: 'completedAssessments', headerName: 'Completed Assessments', align: 'right' },
    {
      field: 'value',
      headerName: 'SFR',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {row.value}%
        </Typography>
      ),
      csvValue: (row) => `${row.value}%`,
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => (row.threshold === null ? <Typography variant="body2" color="text.secondary">Not configured</Typography> : `< ${row.threshold}%`),
      csvValue: (row) => (row.threshold === null ? 'Not configured' : `< ${row.threshold}%`),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="SFR - Slot Fulfillment Rate"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure('sfr-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/sfr-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated} />
      )}

      {!loading && !error && <ThresholdNote note={thresholdNote} month={month} configPath="sfr-config" canSet={canConfigure('sfr-config')} />}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No one has an Assessment Target set for this period."
          exportFileName="SFR"
        />
      )}
    </WarningPage>
  );
};

export default SfrDashboard;
